import { router } from '@router';
import * as Sentry from '@sentry/vue';
import * as MutationTypes from './mutationTypes';

const pointOfSaleListSuccess = (state, payload) => {
  state.pointOfSaleList = payload;
};

const pointOfSaleSettingsSuccess = (state, payload) => {
  state.pointOfSaleSettings = payload;
};

const saleRateListSuccess = (state, payload) => {
  state.saleRateList = payload;
};

const masterSaleFormatListSuccess = (state, payload) => {
  state.masterSaleFormatList = payload;
};

const predictionPeriodListSuccess = (state, payload) => {
  state.predictionPeriodList = payload;
};

const predictionPeriodSuccess = (state, predictionPeriod) => {
  state.predictionPeriodList = state.predictionPeriodList.map((period) => {
    if (period.guid === predictionPeriod.guid) {
      return predictionPeriod;
    }
    return period;
  });
};

const serviceListSuccess = (state, payload) => {
  state.serviceList = payload;
};

const validatePeriodPrices = (state, periodGuid) => {
  state.predictionPeriodList = state.predictionPeriodList.map((period) => {
    if (period.guid === periodGuid) {
      period.areDemandRecommendationsValidated = true;
      period.areProductPriceRecommendationsValidated = true;
    }
    return period;
  });
};

const validatePeriodDemand = (state, periodGuid) => {
  state.predictionPeriodList = state.predictionPeriodList.map((period) => {
    if (period.guid === periodGuid) {
      period.areDemandRecommendationsValidated = true;
    }
    return period;
  });
};

const validatePeriodEngineering = (state, periodGuid) => {
  state.predictionPeriodList = state.predictionPeriodList.map((period) => {
    if (period.guid === periodGuid) period.areMenuEngineeringRecommendationsValidated = true;
    return period;
  });
};

const resetState = (state) => {
  state.predictionPeriodList = [];
  state.predictionPeriodList.cleared = true;
  state.saleRateList = [];
  state.saleZoneList = [];
  state.masterSaleFormatList = [];
  state.serviceList = [];
  state.pointOfSaleSettings = null;
  state.newProducts = null;
  state.masterProductListCancelToken?.cancel();
  state.masterProductList = [];
  state.masterProductProgress = 0.0;
  state.masterProductListCancelToken = null;
  state.groupOfProductPricingList = [];
  state.menuList = [];
};

const modifyNewProducts = (state, payload) => {
  state.newProducts += payload;
};

const newProductsSuccess = (state, payload) => {
  state.newProducts = payload;
};

const masterProductListToken = (state, payload) => {
  state.masterProductListCancelToken = payload;
};

const masterProductListUpdate = (state, { productList, count, pointOfSaleGuid }) => {
  const urlPosGuid = router.history.current.params.pointOfSaleGuid;
  const urlName = router.history.current.name;
  if (urlPosGuid === pointOfSaleGuid || !urlName) {
    state.masterProductProgress += (productList.length / count) * 100;
    state.masterProductList.push(...productList);
  } else {
    Sentry.captureMessage(`Saving products from pos '${pointOfSaleGuid}' while current pos is '${urlPosGuid}'`);
  }
};

const masterProductUpdate = (state, productToUpdate) => {
  const index = state.masterProductList.findIndex((product) => (product.guid === productToUpdate.guid));
  state.masterProductList.splice(index, 1, productToUpdate);
};

const updateMasterSaleFormatSuccess = (state, productToUpdate) => {
  const guidsSet = new Set([productToUpdate.guid, ...Object.values(productToUpdate.sale_formats)]);
  state.masterProductList = state.masterProductList.map((product) => {
    if (guidsSet.has(product.guid)) {
      product.is_menu_enabled = true;
      product.is_new = false;
    }
    return product;
  });
};

const clearToken = (state) => {
  state.masterProductListCancelToken = null;
};

const groupOfProductPricingListSuccess = (state, groupOfProductPricingList) => {
  state.groupOfProductPricingList = groupOfProductPricingList;
};

const menuListSuccess = (state, payload) => {
  state.menuList = payload;
};

const createMenuSuccess = (state, payload) => {
  state.menuList.push(payload);
};

const deleteMenuSuccess = (state, payload) => {
  state.menuList = state.menuList.filter((menu) => menu.guid !== payload.guid);
};

const editMenuSuccess = (state, payload) => {
  const foundIndex = state.menuList.findIndex((menu) => menu.guid === payload.guid);
  state.menuList[foundIndex] = payload;
};

const updateMenuPublication = (state, { menuGuid, updatedPublication }) => {
  const menuToUpdate = state.menuList.find((menu) => menu.guid === menuGuid);
  if (menuToUpdate) menuToUpdate.publication = updatedPublication;
};

export const mutations = {
  [MutationTypes.POINT_OF_SALE_LIST_SUCCESS]: pointOfSaleListSuccess,
  [MutationTypes.SALE_RATE_LIST_SUCCESS]: saleRateListSuccess,
  [MutationTypes.MASTER_SALE_FORMAT_LIST_SUCCESS]: masterSaleFormatListSuccess,
  [MutationTypes.PREDICTION_PERIOD_LIST_SUCCESS]: predictionPeriodListSuccess,
  [MutationTypes.PREDICTION_PERIOD_SUCCESS]: predictionPeriodSuccess,
  [MutationTypes.SERVICE_LIST_SUCCESS]: serviceListSuccess,
  [MutationTypes.VALIDATE_PERIOD_PRICES]: validatePeriodPrices,
  [MutationTypes.VALIDATE_PERIOD_DEMAND]: validatePeriodDemand,
  [MutationTypes.VALIDATE_PERIOD_ENGINEERING]: validatePeriodEngineering,
  [MutationTypes.RESET_POINT_OF_SALE_STATE]: resetState,
  [MutationTypes.POINT_OF_SALE_SETTINGS_SUCCESS]: pointOfSaleSettingsSuccess,
  [MutationTypes.MODIFY_NEW_PRODUCTS]: modifyNewProducts,
  [MutationTypes.NEW_PRODUCTS_SUCCESS]: newProductsSuccess,
  [MutationTypes.MASTER_PRODUCT_LIST_UPDATE]: masterProductListUpdate,
  [MutationTypes.MASTER_PRODUCT_UPDATE]: masterProductUpdate,
  [MutationTypes.MASTER_PRODUCT_LIST_TOKEN]: masterProductListToken,
  [MutationTypes.MASTER_PRODUCT_LIST_SUCCESS]: clearToken,
  [MutationTypes.MASTER_PRODUCT_LIST_ERROR]: clearToken,
  [MutationTypes.MASTER_PRODUCT_SALE_FORMAT_SUCCESS]: updateMasterSaleFormatSuccess,
  [MutationTypes.GROUP_OF_PRODUCT_PRICING_LIST_SUCCESS]: groupOfProductPricingListSuccess,
  [MutationTypes.MENU_LIST_SUCCESS]: menuListSuccess,
  [MutationTypes.CREATE_MENU_SUCCESS]: createMenuSuccess,
  [MutationTypes.EDIT_MENU_SUCCESS]: editMenuSuccess,
  [MutationTypes.DELETE_MENU_SUCCESS]: deleteMenuSuccess,
  [MutationTypes.UPDATE_MENU_PUBLICATION]: updateMenuPublication,
};
